import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FiLock, FiMail, FiShield, FiUser } from "react-icons/fi";
import { auth } from "../config/supabase";

const AdminRegister = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const { name, email, password, confirmPassword } = formData;
    
    // Validation
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }
    
    setLoading(true);
    
    try {
      console.log("🔐 Attempting registration for:", email);
      
      // Create account with Supabase Auth - set as pending approval
      const { data, error } = await auth.createAdminUser(email, password);
      
      if (error) {
        throw error;
      }
      
      console.log("✅ Registration successful - pending approval");
      
      setSuccess(true);
      toast.success("Registration successful! Awaiting admin approval.");
      
      // Clear form
      setFormData({
        name: "",
        email: "",
        password: "",
        confirmPassword: ""
      });
      
    } catch (error) {
      console.error("❌ Registration error:", error);
      
      let errorMessage = "Registration failed. Please try again.";
      
      if (error.message) {
        if (error.message.includes('User already registered')) {
          errorMessage = "An account with this email already exists.";
        } else if (error.message.includes('Invalid email')) {
          errorMessage = "Please enter a valid email address.";
        } else if (error.message.includes('Password should be at least 6 characters')) {
          errorMessage = "Password must be at least 6 characters long.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="bg-[#121212] rounded-xl shadow-lg border border-white/20 overflow-hidden max-w-md w-full p-8">
          <div className="text-center">
            <div className="mb-4 flex justify-center">
              <div className="bg-green-500/10 p-4 rounded-full">
                <FiShield className="text-green-500 text-3xl" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-green-500 mb-4">Registration Submitted!</h1>
            <div className="bg-white/20 p-4 rounded-lg border border-white/30 mb-6">
              <p className="text-white text-sm mb-3">
                Your registration request has been submitted successfully.
              </p>
              <p className="text-gray-300 text-sm mb-3">
                <strong>Next Steps:</strong>
              </p>
              <ul className="text-gray-400 text-xs text-left space-y-1">
                <li>• Your account is pending admin approval</li>
                <li>• You will receive an email when approved</li>
                <li>• You can then login with your credentials</li>
                <li>• Contact support if you need immediate access</li>
              </ul>
            </div>
            <div className="space-y-3">
              <Link
                to="/admin-login"
                className="w-full inline-block text-center py-3 px-4 border border-white rounded-lg text-white hover:bg-white hover:text-black transition-colors"
              >
                Back to Login
              </Link>
              <button
                onClick={() => {
                  setSuccess(false);
                  setFormData({
                    name: "",
                    email: "",
                    password: "",
                    confirmPassword: ""
                  });
                }}
                className="w-full py-3 px-4 bg-gray-700 rounded-lg text-gray-300 hover:bg-gray-600 transition-colors"
              >
                Register Another Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="bg-[#121212] rounded-xl shadow-lg border border-white/20 overflow-hidden max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="bg-white/10 p-4 rounded-full">
            <FiUser className="text-white text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span> Administration
          </h1>
          <p className="text-gray-400 mt-1">Request admin access to the dashboard</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiUser className="text-gray-500" />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="bg-[#0a0a0a] block w-full pl-10 pr-3 py-3 border border-gray-800 rounded-lg focus:ring-white focus:border-white text-white"
                  placeholder="Enter your full name"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="text-gray-500" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-[#0a0a0a] block w-full pl-10 pr-3 py-3 border border-gray-800 rounded-lg focus:ring-white focus:border-white text-white"
                  placeholder="your.email@company.com"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="text-gray-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="bg-[#0a0a0a] block w-full pl-10 pr-3 py-3 border border-gray-800 rounded-lg focus:ring-white focus:border-white text-white"
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="text-gray-500" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="bg-[#0a0a0a] block w-full pl-10 pr-3 py-3 border border-gray-800 rounded-lg focus:ring-white focus:border-white text-white"
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            <div className="bg-white/20 p-3 rounded-lg border border-white/30">
              <p className="text-white text-xs">
                <strong>Note:</strong> Your account will require admin approval before you can access the dashboard.
              </p>
            </div>
            
            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-black bg-white hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Request Access"
                )}
              </button>
            </div>
          </div>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm">
            Already have an account?{" "}
            <Link
              to="/admin-login"
              className="text-white hover:text-white"
            >
              Sign in here
            </Link>
          </p>
        </div>
        
        <div className="mt-8 pt-6 border-t border-gray-800">
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-3">
              <span style={{ fontFamily: 'Playfair Display, serif' }}>Lasmari Chauffeurs</span> Administration
            </p>
            <div className="text-xs text-white bg-white/20 p-3 rounded-lg border border-white/30">
              <p className="font-medium mb-1">🔐 Secure Registration</p>
              <p>Manual approval required</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRegister; 